import * as React from 'react';
import { cn, dayjs, useMediaQuery } from '@noor/lib';
import {
  DateRangePicker,
  standardDateRangePresets,
  type DateRangeValue,
  type DateRangePickerLabels,
  type DateRangePresetLabels,
} from '../date-range-picker';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Dialog, DialogPortal, DialogOverlay, DialogTitle } from '../dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { MenuCalendarIcon, MenuCloseIcon } from '../menu-icons';
import { FilterClearButton } from './filter-clear-button';
import { useFiltersContext } from './filters-context';

/** Russian preset labels — the design's default set. Override via `presetLabels`. */
const DEFAULT_PRESET_LABELS: DateRangePresetLabels = {
  yesterday: 'Вчера',
  today: 'Сегодня',
  thisWeek: 'Эта неделя',
  thisMonth: 'Этот месяц',
  thisYear: 'Этот год',
};

/** Breakpoint (Tailwind `sm`) below which the picker opens as a full-height sheet. */
const DESKTOP_QUERY = '(min-width: 640px)';

export interface FiltersDateRangeProps {
  /** Search param holding the range start (`YYYY-MM-DD`). */
  fromKey: string;
  /** Search param holding the range end (`YYYY-MM-DD`). */
  toKey: string;
  /** Muted prefix before the range in the pill, e.g. "В этом году:". */
  prefix?: string;
  /** Shown in the pill when no range is picked. */
  placeholder?: string;
  /** Locale for the picker's labels (defaults to `ru`). */
  locale?: string;
  /** Full month names (Jan→Dec) — overrides locale-derived names in the calendar. */
  monthNames?: string[];
  /** Monday-first weekday abbreviations (7) — overrides locale-derived ones. */
  weekdayLabels?: string[];
  /** Copy for the picker's "Selected dates" footer (defaults to Russian). */
  pickerLabels?: Partial<DateRangePickerLabels>;
  /** Copy for the quick-pick presets column (defaults to Russian). */
  presetLabels?: DateRangePresetLabels;
  /** Hide the quick-pick presets column (shown by default). */
  hidePresets?: boolean;
  /** Heading shown in the mobile full-height sheet (defaults to Russian). */
  sheetTitle?: string;
  /** Aria-label for the mobile sheet's close button (defaults to Russian). */
  closeLabel?: string;
  'aria-label'?: string;
  className?: string;
}

const PARAM_FORMAT = 'YYYY-MM-DD';
const DISPLAY_FORMAT = 'DD.MM.YYYY';

/**
 * Date-range filter bound to two URL search params, styled as the Figma pill
 * (calendar glyph + prefix + range). Opens the rich `DateRangePicker`, committing
 * the chosen range to the URL. Part of the `Filters` compound family, so a page
 * gets URL-bound range filtering with no navigate wiring.
 *
 * Responsive presentation: from `sm` up the picker opens in an anchored popover;
 * below `sm` it opens as a full-height sheet pinned to the top of the viewport,
 * so the tall calendar always fits the phone screen instead of overflowing below
 * the trigger.
 */
export function FiltersDateRange({
  fromKey,
  toKey,
  prefix,
  placeholder,
  locale = 'ru',
  monthNames,
  weekdayLabels,
  pickerLabels,
  presetLabels = DEFAULT_PRESET_LABELS,
  hidePresets = false,
  sheetTitle = 'Выбор дат',
  closeLabel = 'Закрыть',
  className,
  ...aria
}: FiltersDateRangeProps) {
  const { getParam, setParam } = useFiltersContext();
  const [open, setOpen] = React.useState(false);
  const isDesktop = useMediaQuery(DESKTOP_QUERY);

  const from = parse(getParam(fromKey));
  const to = parse(getParam(toKey));
  const value: DateRangeValue | undefined = from
    ? { from, ...(to ? { to } : {}) }
    : undefined;

  const label =
    from && to
      ? `${dayjs(from).format(DISPLAY_FORMAT)} - ${dayjs(to).format(DISPLAY_FORMAT)}`
      : from
        ? dayjs(from).format(DISPLAY_FORMAT)
        : placeholder;

  const presets = hidePresets ? [] : standardDateRangePresets(presetLabels);

  const handleApply = (range: DateRangeValue): void => {
    setParam(fromKey, dayjs(range.from).format(PARAM_FORMAT));
    setParam(toKey, range.to ? dayjs(range.to).format(PARAM_FORMAT) : undefined);
    setOpen(false);
  };

  const clear = (): void => {
    setParam(fromKey, undefined);
    setParam(toKey, undefined);
  };

  const ariaLabel = aria['aria-label'] ?? placeholder;
  const hasValue = from != null;

  const picker = (
    <DateRangePicker
      value={value}
      onApply={handleApply}
      presets={presets}
      locale={locale}
      {...(monthNames ? { monthNames } : {})}
      {...(weekdayLabels ? { weekdayLabels } : {})}
      {...(pickerLabels ? { labels: pickerLabels } : {})}
    />
  );

  const trigger = (
    <button
      type="button"
      aria-label={ariaLabel}
      className={cn(
        // Figma date pill: h-10 white rounded-full, 16px icon + muted prefix +
        // black range in 14px medium. `w-full` fills the wrapper, so callers
        // control width via the wrapper `className` (e.g. `w-full sm:w-fit`
        // to stretch on mobile then shrink to content on the row).
        'flex h-10 w-full items-center gap-2.5 rounded-full border border-filter-border bg-filter-bg px-4 text-sm font-medium tracking-[-0.28px] whitespace-nowrap shadow-field transition-colors hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none data-[state=open]:border-input-border-focus',
        hasValue && 'pr-10',
      )}
    >
      <MenuCalendarIcon className="size-4 shrink-0 text-primary" />
      <span className={cn('truncate', !hasValue && 'font-normal')}>
        {prefix ? (
          <span className="font-normal text-filter-label-fg">{prefix} </span>
        ) : null}
        <span className="text-filter-value-fg">{label}</span>
      </span>
    </button>
  );

  const clearButton = hasValue ? (
    <FilterClearButton
      onClear={clear}
      aria-label={`${ariaLabel} — clear`}
      className="absolute top-1/2 right-3 -translate-y-1/2"
    />
  ) : null;

  // ── Desktop (sm+): anchored popover ──
  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <div className={cn('relative w-fit', className)}>
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          {clearButton}
        </div>
        <PopoverContent
          className="max-h-[calc(100vh-4rem)] w-auto max-w-[calc(100vw-2rem)] overflow-y-auto bg-muted p-2"
          align="start"
        >
          {picker}
        </PopoverContent>
      </Popover>
    );
  }

  // ── Mobile (< sm): full-height sheet pinned to the top of the viewport ──
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div className={cn('relative w-full', className)}>
        <DialogPrimitive.Trigger asChild>{trigger}</DialogPrimitive.Trigger>
        {clearButton}
      </div>
      <DialogPortal>
        <DialogOverlay />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            'fixed inset-x-0 top-0 z-50 flex h-dvh flex-col bg-muted text-foreground outline-none',
            'data-[state=closed]:animate-out data-[state=closed]:slide-out-to-top data-[state=open]:animate-in data-[state=open]:slide-in-from-top',
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <DialogTitle className="text-base font-semibold text-foreground">
              {sheetTitle}
            </DialogTitle>
            <DialogPrimitive.Close
              aria-label={closeLabel}
              className="flex size-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-card focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none"
            >
              <MenuCloseIcon className="size-5" />
            </DialogPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">{picker}</div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

/** `YYYY-MM-DD` param → `Date` (undefined when unset/invalid). */
function parse(raw: string | undefined): Date | undefined {
  if (!raw) return undefined;
  const d = dayjs(raw, PARAM_FORMAT);
  return d.isValid() ? d.toDate() : undefined;
}
